import { Controller } from "@hotwired/stimulus"

// Controls the X-17 ordered shift-code cascade editor.
// HTML5 drag-and-drop, no external library.
//
// Targets:
//   list       — <ul> holding the ordered items
//   item       — each draggable <li>
//   rank       — numeric badge inside an item (re-numbered on reorder)
//   addSelect  — <select> next to the [+] add button
//
// Values:
//   paramKey   — planning_rule[params][:cascade] -> "cascade"
export default class extends Controller {
  static targets = ["list", "item", "rank", "addSelect"]
  static values = { paramKey: String }

  connect() {
    this.dragSrc = null
  }

  add() {
    if (!this.hasAddSelectTarget) return
    const select = this.addSelectTarget
    const code = select.value
    if (!code) return

    const li = document.createElement("li")
    li.setAttribute("data-cascade-order-target", "item")
    li.setAttribute("draggable", "true")
    li.setAttribute(
      "data-action",
      "dragstart->cascade-order#dragStart dragover->cascade-order#dragOver drop->cascade-order#drop dragend->cascade-order#dragEnd"
    )
    li.className =
      "flex items-center gap-2 px-2 py-1.5 bg-white border border-gray-200 rounded-lg text-xs cursor-move hover:border-teal-300"
    li.innerHTML = `
      <i class="fas fa-grip-vertical text-gray-400"></i>
      <span class="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-teal-700 bg-teal-50 rounded-full" data-cascade-order-target="rank"></span>
      <span class="font-mono font-semibold text-gray-900">${code}</span>
      <input type="hidden" name="planning_rule[params][${this.paramKeyValue}][]" value="${code}">
      <button type="button" data-action="cascade-order#remove" class="ml-auto text-red-500 hover:text-red-700" aria-label="Verwijder ${code}">
        <i class="fas fa-times text-xs"></i>
      </button>
    `
    this.listTarget.appendChild(li)

    // Remove from the dropdown so we can't add it twice
    const option = select.querySelector(`option[value="${CSS.escape(code)}"]`)
    if (option) option.remove()
    select.value = ""

    this.renumber()
  }

  remove(event) {
    const item = event.target.closest("[data-cascade-order-target='item']")
    if (!item) return
    const codeNode = item.querySelector("input[type='hidden']")
    const code = codeNode ? codeNode.value : null
    item.remove()

    if (code && this.hasAddSelectTarget) {
      const opt = document.createElement("option")
      opt.value = code
      opt.textContent = code
      this.addSelectTarget.appendChild(opt)
    }

    this.renumber()
  }

  dragStart(event) {
    this.dragSrc = event.currentTarget
    event.dataTransfer.effectAllowed = "move"
    event.currentTarget.classList.add("opacity-50")
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
  }

  drop(event) {
    event.preventDefault()
    const target = event.currentTarget
    if (!this.dragSrc || this.dragSrc === target) return

    const items = Array.from(this.listTarget.children)
    const srcIdx = items.indexOf(this.dragSrc)
    const tgtIdx = items.indexOf(target)

    if (srcIdx < tgtIdx) {
      target.parentNode.insertBefore(this.dragSrc, target.nextSibling)
    } else {
      target.parentNode.insertBefore(this.dragSrc, target)
    }

    this.renumber()
  }

  dragEnd(event) {
    event.currentTarget.classList.remove("opacity-50")
    this.dragSrc = null
  }

  renumber() {
    this.rankTargets.forEach((node, idx) => {
      node.textContent = idx + 1
    })
  }
}
